import { GuardianDetail, Nominee } from '../../../proto/generated/trading-bridge'

export const MAX_NOMINEES = 3
export const NOMINEE_MINOR_AGE = 18

export const NOMINEE_RELATIONSHIP_OTHER = 'Other'

export const NOMINEE_RELATIONSHIP_OPTIONS = [
    'Wife',
    'Husband',
    'Son',
    'Daughter',
    'Mother',
    'Father',
    'Sister',
    'Brother',
    NOMINEE_RELATIONSHIP_OTHER
] as const

export const GUARDIAN_RELATIONSHIP_OPTIONS = [
    'Father',
    'Mother',
    'Brother',
    'Sister',
    'Grand Mother',
    'Grand Father',
    NOMINEE_RELATIONSHIP_OTHER
] as const

export type NomineeRelationshipOption = (typeof NOMINEE_RELATIONSHIP_OPTIONS)[number]

export const isPresetRelationship = (relationship: string, options: readonly string[]): boolean =>
    options.slice(0, -1).some(
        (option) => option.toLowerCase() === relationship.trim().toLowerCase()
    )

export const isCustomRelationship = (relationship: string, options: readonly string[]): boolean =>
    relationship.trim() !== '' && !isPresetRelationship(relationship, options)

export const getRelationshipSelectValue = (relationship: string, options: readonly string[]): string => {
    const trimmed = relationship.trim()
    if (!trimmed) return ''
    const preset = options.slice(0, -1).find(
        (option) => option.toLowerCase() === trimmed.toLowerCase()
    )
    return preset ?? options[options.length - 1]
}

export const getCustomRelationshipValue = (relationship: string, options: readonly string[]): string =>
    isCustomRelationship(relationship, options) ? relationship.trim() : ''

export const isPresetNomineeRelationship = (relationship: string): boolean =>
    isPresetRelationship(relationship, NOMINEE_RELATIONSHIP_OPTIONS)

export const isCustomNomineeRelationship = (relationship: string): boolean =>
    isCustomRelationship(relationship, NOMINEE_RELATIONSHIP_OPTIONS)

export const getNomineeRelationshipSelectValue = (relationship: string): string =>
    getRelationshipSelectValue(relationship, NOMINEE_RELATIONSHIP_OPTIONS)

export const getNomineeCustomRelationshipValue = (relationship: string): string =>
    getCustomRelationshipValue(relationship, NOMINEE_RELATIONSHIP_OPTIONS)

export interface NomineeFieldErrors {
    name?: string
    relationship?: string
    dob?: string
    allocationPercent?: string
    guardianName?: string
    guardianRelationship?: string
}

export interface NomineeFormValidation {
    nomineeErrors: NomineeFieldErrors[]
    formError?: string
}

const getNomineeRowFieldErrors = (nominee: Nominee): NomineeFieldErrors => {
    const errors: NomineeFieldErrors = {}

    if (!nominee.name.trim()) {
        errors.name = 'Name is required'
    }
    if (!nominee.relationship.trim()) {
        errors.relationship = 'Relationship is required'
    }
    if (!nominee.dob.trim()) {
        errors.dob = 'Date of birth is required'
    } else if (!parseNomineeDob(nominee.dob)) {
        errors.dob = 'Enter a valid date of birth'
    } else {
        const age = getNomineeAge(nominee.dob)
        if (age !== null && age < 0) {
            errors.dob = 'Date of birth cannot be in the future'
        }
    }
    if (!nominee.allocationPercent || nominee.allocationPercent < 1 || nominee.allocationPercent > 100) {
        errors.allocationPercent = 'Enter allocation between 1 and 100'
    }

    if (isNomineeMinor(nominee.dob)) {
        if (!nominee.guardian?.name.trim()) {
            errors.guardianName = 'Guardian name is required'
        }
        if (!nominee.guardian?.relationship.trim()) {
            errors.guardianRelationship = 'Guardian relationship is required'
        }
    }

    return errors
}

export const getNomineeFormValidation = (nominees: Nominee[]): NomineeFormValidation => {
    const nomineeErrors = nominees.map(getNomineeRowFieldErrors)
    const filled = nominees.filter((n) => n.name.trim())

    if (filled.length === 0) {
        return {
            nomineeErrors,
            formError: nominees.length === 0 ? 'Add at least one nominee' : undefined
        }
    }

    const total = getAllocationTotal(filled)
    if (total !== 100) {
        return {
            nomineeErrors,
            formError: `Nominee allocation must sum to 100% (currently ${total}%)`
        }
    }

    return { nomineeErrors }
}

export const hasNomineeFormErrors = (validation: NomineeFormValidation): boolean => {
    if (validation.formError) return true
    return validation.nomineeErrors.some((errors) => Object.keys(errors).length > 0)
}

export const emptyNominee = (): Nominee => ({
    id: 0,
    nomineeOrder: 0,
    name: '',
    relationship: '',
    allocationPercent: 0,
    dob: '',
    pan: '',
    address: '',
    minor: false,
    guardian: { name: '', relationship: '', pan: '' },
    proofDocumentId: 0
})

const emptyGuardian = (): GuardianDetail => ({ name: '', relationship: '', pan: '' })

export const parseNomineeDob = (dob: string): Date | null => {
    if (!dob.trim()) return null
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob.trim())
    if (!match) return null
    const year = Number(match[1])
    const month = Number(match[2]) - 1
    const day = Number(match[3])
    const parsed = new Date(year, month, day)
    if (
        parsed.getFullYear() !== year ||
        parsed.getMonth() !== month ||
        parsed.getDate() !== day
    ) {
        return null
    }
    return parsed
}

export const getNomineeAge = (dob: string, asOf: Date = new Date()): number | null => {
    const birthDate = parseNomineeDob(dob)
    if (!birthDate) return null
    let age = asOf.getFullYear() - birthDate.getFullYear()
    const monthDiff = asOf.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && asOf.getDate() < birthDate.getDate())) {
        age--
    }
    return age
}

export const isNomineeMinor = (dob: string, asOf: Date = new Date()): boolean => {
    const age = getNomineeAge(dob, asOf)
    return age !== null && age < NOMINEE_MINOR_AGE
}

export const syncNomineeMinorFromDob = (nominee: Nominee): Nominee => {
    const minor = isNomineeMinor(nominee.dob)
    return {
        ...nominee,
        minor,
        guardian: minor ? (nominee.guardian ?? emptyGuardian()) : emptyGuardian()
    }
}

export const normalizeNominees = (nominees: Nominee[]): Nominee[] =>
    nominees.map((n) =>
        syncNomineeMinorFromDob({
            ...n,
            guardian: n.guardian ?? emptyGuardian()
        })
    )

export const cloneNomineesForForm = (nominees: Nominee[]): Nominee[] =>
    normalizeNominees(nominees).map((n) => ({
        ...n,
        guardian: { ...(n.guardian as GuardianDetail) }
    }))

export const getAllocationTotal = (nominees: Nominee[]): number =>
    nominees.reduce((sum, n) => sum + (Number(n.allocationPercent) || 0), 0)

export const buildNomineeRequestPayload = (nominees: Nominee[]): Nominee[] => {
    const filled = nominees.filter((n) => n.name.trim()).map(syncNomineeMinorFromDob)
    return filled.map((n, index) => ({
        ...n,
        nomineeOrder: index + 1,
        guardian: n.minor ? (n.guardian ?? emptyGuardian()) : emptyGuardian()
    }))
}

export const validateNominees = (nominees: Nominee[]): string | null => {
    const filled = nominees.filter((n) => n.name.trim())
    if (filled.length === 0) {
        return 'Add at least one nominee'
    }

    for (const nominee of filled) {
        if (!nominee.relationship.trim()) {
            return `Relationship is required for ${nominee.name || 'nominee'}`
        }
        if (!nominee.dob.trim()) {
            return `Date of birth is required for ${nominee.name}`
        }
        if (!parseNomineeDob(nominee.dob)) {
            return `Enter a valid date of birth for ${nominee.name}`
        }
        const age = getNomineeAge(nominee.dob)
        if (age !== null && age < 0) {
            return `Date of birth cannot be in the future for ${nominee.name}`
        }
        if (isNomineeMinor(nominee.dob) && !nominee.guardian?.name.trim()) {
            return `Guardian name is required for minor nominee ${nominee.name}`
        }
        if (isNomineeMinor(nominee.dob) && !nominee.guardian?.relationship.trim()) {
            return `Guardian relationship is required for minor nominee ${nominee.name}`
        }
    }

    const total = getAllocationTotal(filled)
    if (total !== 100) {
        return `Nominee allocation must sum to 100% (currently ${total}%)`
    }
    return null
}

export const nomineeIsMinor = (nominee: Nominee): boolean =>
    nominee.minor || isNomineeMinor(nominee.dob)
